import React, { useEffect, useState, useMemo } from 'react';
import { Button } from '@material-ui/core';
import { httpClient } from '../utils/dataProvider';
import {
    ActionButton, Empty, MetricCard, PageHeader, Row, RowMeta,
    RowText, RowTitle, Search, StatusPill, Surface, Toolbar
} from '../components/ui';

const TABS = ['ALL', 'ACTIVE_DEBT', 'SUSPENDED'];

export default function DebtDashboard() {
    const [debtors, setDebtors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [tab, setTab] = useState('ACTIVE_DEBT');
    const [search, setSearch] = useState('');
    const [detail, setDetail] = useState(null);
    const [events, setEvents] = useState([]);
    const [eventsLoading, setEventsLoading] = useState(false);

    // Override form
    const [overrideAction, setOverrideAction] = useState('');
    const [overrideNote, setOverrideNote] = useState('');
    const [overrideAmount, setOverrideAmount] = useState('');
    const [overrideDeadline, setOverrideDeadline] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);

    const load = () => {
        setLoading(true);
        httpClient('https://skilzapp.net/api/provider-debt').then(({ json }) => {
            setDebtors(json || []);
        }).catch(console.error).finally(() => setLoading(false));
    };

    useEffect(load, []);

    const loadEvents = (userId) => {
        setEventsLoading(true);
        httpClient(`https://skilzapp.net/api/provider-debt/${userId}/events`).then(({ json }) => {
            setEvents(json || []);
        }).catch(console.error).finally(() => setEventsLoading(false));
    };

    const filtered = useMemo(() => {
        let list = debtors;
        if (tab === 'ACTIVE_DEBT') list = list.filter(d => d.debt?.amount > 0 && !d.debt?.suspendedForDebt);
        else if (tab === 'SUSPENDED') list = list.filter(d => d.debt?.suspendedForDebt);

        if (search) {
            const q = search.toLowerCase();
            list = list.filter(d =>
                (d.name || '').toLowerCase().includes(q) ||
                (d.phone || '').includes(q)
            );
        }
        return list;
    }, [debtors, tab, search]);

    const counts = useMemo(() => ({
        ALL: debtors.length,
        ACTIVE_DEBT: debtors.filter(d => d.debt?.amount > 0 && !d.debt?.suspendedForDebt).length,
        SUSPENDED: debtors.filter(d => d.debt?.suspendedForDebt).length,
    }), [debtors]);

    const totalDebt = useMemo(() =>
        debtors.reduce((sum, d) => sum + (d.debt?.amount || 0), 0),
        [debtors]);

    const handleOverride = () => {
        if (!overrideAction || !overrideNote.trim()) return;
        setSubmitting(true);
        const body = { action: overrideAction, note: overrideNote };
        if (overrideAction === 'ADJUST' && overrideAmount) body.amount = parseInt(overrideAmount, 10);
        if (overrideAction === 'EXTEND_DEADLINE' && overrideDeadline) body.newDeadline = overrideDeadline;

        httpClient(`https://skilzapp.net/api/provider-debt/${detail._id}/override`, {
            method: 'POST',
            body: JSON.stringify(body)
        }).then(() => {
            setResult('Override applied successfully.');
            setOverrideAction('');
            setOverrideNote('');
            setOverrideAmount('');
            setOverrideDeadline('');
            load();
            loadEvents(detail._id);
        }).catch(() => setResult('Override failed.')).finally(() => setSubmitting(false));
    };

    const handleProcessCycle = () => {
        httpClient('https://skilzapp.net/api/provider-debt/process-cycle', { method: 'POST' })
            .then(({ json }) => {
                alert(`Debt cycle processed: ${json.processed} checked, ${json.reminders} reminders sent, ${json.suspended} suspended.`);
                load();
            }).catch(console.error);
    };

    // ── Detail View ──
    if (detail) {
        const ds = detail.debtStatus || {};
        return <main className="sk-page">
            <PageHeader eyebrow="Debt Management" title={detail.name || 'Unknown'} description={detail.phone} actions={<ActionButton variant="secondary" onClick={() => { setDetail(null); setEvents([]); setResult(null); }}>← Back</ActionButton>} />

            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
                <MetricCard tone={ds.suspended ? 'red' : 'orange'} label="Debt" value={`$${((ds.amount || 0) / 100).toFixed(2)}`} detail={ds.currency || 'USD'} />
                <MetricCard tone="blue" label="Days Remaining" value={ds.daysRemaining ?? '—'} detail={ds.deadline ? `Deadline: ${new Date(ds.deadline).toLocaleDateString()}` : 'No deadline'} />
                <MetricCard label="Reminders Sent" value={ds.remindersSent || 0} detail="push notifications" />
                <MetricCard tone={ds.suspended ? 'red' : 'green'} label="Status" value={ds.suspended ? 'SUSPENDED' : 'Active'} detail={detail.status} />
            </div>

            {/* Override controls */}
            <Surface title="Admin Override" description="Clear debt, adjust amount, extend deadline, or unsuspend this provider.">
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <div>
                        <label style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Action</label>
                        <select value={overrideAction} onChange={e => setOverrideAction(e.target.value)} style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #ccc' }}>
                            <option value="">Select…</option>
                            <option value="CLEAR">Clear Debt</option>
                            <option value="ADJUST">Adjust Amount</option>
                            <option value="EXTEND_DEADLINE">Extend Deadline</option>
                            <option value="UNSUSPEND">Unsuspend</option>
                        </select>
                    </div>
                    {overrideAction === 'ADJUST' && <div>
                        <label style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>New Amount (cents)</label>
                        <input type="number" value={overrideAmount} onChange={e => setOverrideAmount(e.target.value)} placeholder="e.g. 500" style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #ccc', width: 120 }} />
                    </div>}
                    {overrideAction === 'EXTEND_DEADLINE' && <div>
                        <label style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>New Deadline</label>
                        <input type="date" value={overrideDeadline} onChange={e => setOverrideDeadline(e.target.value)} style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #ccc' }} />
                    </div>}
                    <div style={{ flex: 1, minWidth: 200 }}>
                        <label style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Note (required)</label>
                        <input value={overrideNote} onChange={e => setOverrideNote(e.target.value)} placeholder="Reason for override…" style={{ width: '100%', padding: '6px 10px', borderRadius: 8, border: '1px solid #ccc' }} />
                    </div>
                    <ActionButton disabled={submitting || !overrideAction || !overrideNote.trim()} onClick={handleOverride}>{submitting ? 'Applying…' : 'Apply Override'}</ActionButton>
                </div>
                {result && <p style={{ marginTop: 8, fontSize: 13, color: result.includes('failed') ? '#d32f2f' : '#388e3c' }}>{result}</p>}
            </Surface>

            {/* Audit trail */}
            <Surface title="Debt Audit Trail" description="Immutable event history for this provider's debt lifecycle.">
                {eventsLoading && <div className="sk-state is-loading" role="status">Loading events…</div>}
                {!eventsLoading && !events.length && <Empty>No debt events recorded.</Empty>}
                {events.length > 0 && <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                    <thead><tr style={{ borderBottom: '2px solid #eee' }}>
                        <th style={{ textAlign: 'left', padding: 6 }}>Action</th>
                        <th style={{ textAlign: 'right', padding: 6 }}>Debt</th>
                        <th style={{ textAlign: 'right', padding: 6 }}>Payment</th>
                        <th style={{ textAlign: 'left', padding: 6 }}>Note</th>
                        <th style={{ textAlign: 'left', padding: 6 }}>By</th>
                        <th style={{ textAlign: 'left', padding: 6 }}>Date</th>
                    </tr></thead>
                    <tbody>{events.map(e => <tr key={e._id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: 6 }}><StatusPill status={
                            e.action === 'AUTO_SUSPENDED' ? 'banned' :
                            e.action === 'DEBT_CLEARED' ? 'active' :
                            e.action === 'ADMIN_UNSUSPEND' ? 'active' : 'warning'
                        }>{e.action}</StatusPill></td>
                        <td style={{ textAlign: 'right', padding: 6 }}>${((e.debtAmount || 0) / 100).toFixed(2)}</td>
                        <td style={{ textAlign: 'right', padding: 6 }}>{e.paymentAmount != null ? `$${(e.paymentAmount / 100).toFixed(2)}` : '—'}</td>
                        <td style={{ padding: 6, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.note || (e.reminderDay ? `Day ${e.reminderDay} reminder` : '—')}</td>
                        <td style={{ padding: 6 }}>{e.performedBy?.name || 'System'}</td>
                        <td style={{ padding: 6 }}>{e.createdAt ? new Date(e.createdAt).toLocaleString() : '—'}</td>
                    </tr>)}</tbody>
                </table>}
            </Surface>
        </main>;
    }

    // ── List View ──
    return <main className="sk-page">
        <PageHeader eyebrow="Financial Operations" title="Provider Debt Dashboard" description="Monitor and manage provider debt, reminders, and auto-suspensions."
            actions={<ActionButton variant="secondary" onClick={handleProcessCycle}>⚡ Run Debt Cycle</ActionButton>} />

        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
            <MetricCard tone="orange" label="Total Debtors" value={debtors.length} detail="Providers with outstanding debt" />
            <MetricCard tone="red" label="Total Debt" value={`$${(totalDebt / 100).toFixed(2)}`} detail="Combined outstanding amount" />
            <MetricCard tone="blue" label="Active Debt" value={counts.ACTIVE_DEBT} detail="Not yet suspended" />
            <MetricCard tone="red" label="Suspended" value={counts.SUSPENDED} detail="Auto-suspended for non-payment" />
        </div>

        <Surface title="Debtors" description={`${filtered.length} providers`}>
            <Toolbar right={<Search value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name or phone…" />}>
                {TABS.map(t => <Button key={t} aria-pressed={tab === t} variant={tab === t ? 'contained' : 'outlined'} color="primary" size="small" onClick={() => setTab(t)}>
                    {t === 'ALL' ? 'All' : t === 'ACTIVE_DEBT' ? 'Active Debt' : 'Suspended'} ({counts[t]})
                </Button>)}
            </Toolbar>
        </Surface>

        {loading && <div className="sk-state is-loading" role="status">Loading debtors…</div>}
        {!loading && !filtered.length && <Empty>No providers with outstanding debt.</Empty>}

        {filtered.map(d => {
            const ds = d.debtStatus || {};
            return <Row key={d._id} right={
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span style={{ fontWeight: 600, color: '#d32f2f' }}>${((ds.amount || 0) / 100).toFixed(2)}</span>
                    <StatusPill status={ds.suspended ? 'banned' : 'warning'}>{ds.suspended ? 'SUSPENDED' : `${ds.daysRemaining ?? '?'}d left`}</StatusPill>
                </div>
            }>
                <RowText>
                    <RowTitle><a href="#" onClick={e => { e.preventDefault(); setDetail(d); loadEvents(d._id); }}>{d.name || 'Unknown'}</a></RowTitle>
                    <RowMeta>{d.phone || '—'} · Since: {ds.since ? new Date(ds.since).toLocaleDateString() : '—'}</RowMeta>
                    <RowMeta dim>Reminders: {ds.remindersSent || 0} · Deadline: {ds.deadline ? new Date(ds.deadline).toLocaleDateString() : '—'}</RowMeta>
                </RowText>
            </Row>;
        })}
    </main>;
}
